import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { useBranding } from "@/lib/branding";
import { BrandMark } from "@/components/LogoUploader";
import { Field } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function AcceptInvite() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const reset = sp.get("mode") === "reset";
  const nav = useNavigate();
  const { setSession } = useAuth();
  const { logo } = useBranding();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${encodeURIComponent(token)}`).then((r) => setInfo(r.data)).catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return toast.error("La password deve avere almeno 8 caratteri");
    if (pwd !== pwd2) return toast.error("Le due password non coincidono");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password: pwd });
      setSession(data);
      toast.success(reset ? "Password aggiornata, bentornato!" : "Account attivato, benvenuto!");
      nav(homeFor(data), { replace: true });
    } catch (x) {
      toast.error(errMsg(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F6F7F9] p-6">
      <div className="fade-up w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8">
        <div className="-mx-8 -mt-8 mb-8 rounded-t-2xl border-b border-slate-200 bg-blue-50 px-8 py-5"><BrandMark logo={logo} dark /></div>
        {error ? (
          <div data-testid="invite-error" className="space-y-4">
            <h1 className="font-display text-2xl font-extrabold text-slate-900">Link non valido</h1>
            <p className="text-sm text-slate-600">{error}</p>
            <Button asChild variant="outline"><Link to="/login">Vai all'accesso</Link></Button>
          </div>
        ) : !info ? (
          <div className="text-sm text-slate-500">Verifica del link…</div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="invite-form">
            <div>
              <h1 className="font-display text-2xl font-extrabold text-slate-900">{reset ? "Reimposta la password" : "Attiva il tuo account"}</h1>
              <p className="mt-1 text-sm text-slate-600">
                Ciao <b>{info.name}</b>, scegli {reset ? "la nuova password" : "la password"} per accedere a <b>{info.scope_name}</b>.
              </p>
              <p data-testid="invite-email" className="mt-1 font-mono text-xs text-slate-500">{info.email}</p>
            </div>
            <Field label="Nuova password (min. 8 caratteri)">
              <Input data-testid="invite-password-input" type="password" autoComplete="new-password" value={pwd} onChange={(e) => setPwd(e.target.value)} />
            </Field>
            <Field label="Ripeti la password">
              <Input data-testid="invite-password2-input" type="password" autoComplete="new-password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} />
            </Field>
            <Button data-testid="invite-submit-button" disabled={busy} className="h-11 w-full bg-sky-600 hover:bg-sky-700">
              <KeyRound className="mr-2 h-4 w-4" />{busy ? "Salvataggio…" : reset ? "Salva e accedi" : "Attiva e accedi"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
